import { useEffect, useRef, useState } from 'react';
import { StanModal } from '@/components';
import { copyToClipboard, createIconElement } from '@/lib';
import { useUser } from '@/queries/useUser';
import { genFunnelURL } from '../../../logic';
import { useAddFunnelToStore } from '../../../hooks/useAddFunnelToStore';
import IcCopy from '@/assets/icons/ic-copy.svg';
import IcCheck from '@/assets/icons/ic-check.svg';
import IcMail from '@/assets/icons/ic-mail.svg';
import IcFacebook from '@/assets/icons/socials/facebook.svg';
import IcInstagram from '@/assets/icons/ic-instagram.svg';
import IcLink from '@/assets/icons/ic-link.svg';
import IcWarning from '@/assets/icons/ic-warning-circle.svg';
import IcFunnel from '@/assets/icons/ic-funnel.svg';

interface ShareFunnelModalProps {
    isOpen: boolean;
    onClose: () => void;
    storeId: number;
    slug: string;
    funnelName: string;
    publishedPageCount: number;
}

function buildShareLinks(url: string, funnelName: string) {
    const encoded = encodeURIComponent(url);
    const text = encodeURIComponent(`Check out my funnel: ${funnelName}`);
    return {
        twitter: `https://twitter.com/intent/tweet?url=${encoded}&text=${text}`,
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encoded}`,
        instagram: `https://www.instagram.com/`,
        email: `mailto:?subject=${encodeURIComponent(funnelName)}&body=${encodeURIComponent(`Check out my funnel: ${url}`)}`,
    };
}

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
    <p className='text-caption-2-bold text-gray tracking-[0.08em] uppercase'>{children}</p>
);

export const ShareFunnelModal = ({
    isOpen,
    onClose,
    storeId,
    slug,
    funnelName,
    publishedPageCount,
}: ShareFunnelModalProps) => {
    const { user } = useUser();
    const username = user?.username ?? '';
    const funnelUrl = genFunnelURL(slug, username);
    const shareLinks = buildShareLinks(funnelUrl, funnelName);
    const addToStore = useAddFunnelToStore();

    const handleAddToStore = () => {
        addToStore({ storeId, slug, name: funnelName, url: funnelUrl });
    };

    const isNotReady = publishedPageCount === 0;
    const isThin = publishedPageCount === 1;

    const [copiedUrl, setCopiedUrl] = useState(false);
    const urlTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (urlTimerRef.current) clearTimeout(urlTimerRef.current);
        };
    }, []);

    const handleCopyUrl = () => {
        copyToClipboard(funnelUrl, { successMessage: 'Link copied!' });
        setCopiedUrl(true);
        if (urlTimerRef.current) clearTimeout(urlTimerRef.current);
        urlTimerRef.current = setTimeout(() => setCopiedUrl(false), 3000);
    };

    return (
        <StanModal id='share-funnel-modal' isOpen={isOpen} onClose={onClose} title='Share Funnel'>
            <div className='flex flex-col gap-5'>
                {/* Readiness banners */}
                {isNotReady && (
                    <div className='bg-negative-subtle border-negative flex items-start gap-2.5 rounded-md border px-3 py-2.5'>
                        {createIconElement(IcWarning, { className: 'text-negative mt-0.5 size-4 shrink-0' })}
                        <p className='text-body-sm text-negative'>
                            This funnel has no published pages yet. Add and publish at least one product before sharing.
                        </p>
                    </div>
                )}
                {!isNotReady && isThin && (
                    <div className='bg-warning-subtle border-warning flex items-start gap-2.5 rounded-md border px-3 py-2.5'>
                        {createIconElement(IcWarning, { className: 'text-warning mt-0.5 size-4 shrink-0' })}
                        <p className='text-body-sm text-warning'>
                            Your funnel only has one page. Consider adding an upsell or downsell to increase revenue per
                            buyer.
                        </p>
                    </div>
                )}

                {/* Copy link */}
                <div className='flex flex-col gap-2'>
                    <SectionLabel>Copy link</SectionLabel>
                    <div className='bg-canvas border-neutral-subtle flex items-center gap-2 rounded-md border px-3 py-2.5'>
                        {createIconElement(IcLink, { className: 'text-gray size-4 shrink-0' })}
                        <span className='text-body-sm text-dark min-w-0 flex-1 truncate'>{funnelUrl}</span>
                        <button
                            type='button'
                            onClick={handleCopyUrl}
                            aria-label='Copy funnel link'
                            title={copiedUrl ? 'Copied!' : 'Copy link'}
                            className='text-brand-main focus-visible:ring-brand-main flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-sm transition-colors outline-none focus-visible:ring-2 focus-visible:ring-offset-2'
                        >
                            {copiedUrl
                                ? createIconElement(IcCheck, { className: 'size-5 text-positive' })
                                : createIconElement(IcCopy, { className: 'size-5 text-brand-dark' })}
                        </button>
                    </div>
                </div>

                {/* Social sharing */}
                <div className='flex flex-col gap-2'>
                    <SectionLabel>Share on</SectionLabel>
                    <div className='border-neutral-subtle divide-neutral-subtle divide-y rounded-md border'>
                        <SocialRow
                            href={shareLinks.twitter}
                            label='Twitter / X'
                            icon={<span className='text-label-md-bold text-dark leading-none'>𝕏</span>}
                        />
                        <SocialRow
                            href={shareLinks.facebook}
                            label='Facebook'
                            icon={<IcFacebook className='size-5' />}
                        />
                        <SocialRow
                            href={shareLinks.instagram}
                            label='Instagram (bio link)'
                            icon={createIconElement(IcInstagram, { className: 'size-4 text-dark' })}
                        />
                        <SocialRow
                            href={shareLinks.email}
                            label='Email'
                            icon={createIconElement(IcMail, { className: 'size-5 text-dark' })}
                        />
                    </div>
                </div>

                {/* Add to My Store — redirects to the legacy URL/Media create page with the funnel pre-filled. */}
                <div className='border-neutral-subtle border-t pt-4'>
                    <button
                        type='button'
                        onClick={handleAddToStore}
                        disabled={isNotReady}
                        className='border-neutral-subtle hover:border-brand-muted hover:bg-brand-subtle group flex w-full cursor-pointer items-center gap-3 rounded-md border p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60'
                    >
                        <span className='bg-brand-subtle text-brand-main group-hover:bg-brand-soft flex size-9 shrink-0 items-center justify-center rounded-md transition-colors'>
                            {createIconElement(IcFunnel, { className: 'size-5' })}
                        </span>
                        <div className='flex flex-col gap-0.5'>
                            <span className='text-label-sm-bold text-dark'>Add to My Store</span>
                            <span className='text-body-xs text-gray'>
                                Publish this funnel as a link on your Stan store page.
                            </span>
                        </div>
                    </button>
                </div>
            </div>
        </StanModal>
    );
};

const SocialRow = ({ href, label, icon }: { href: string; label: string; icon: React.ReactNode }) => (
    <a
        href={href}
        target='_blank'
        rel='noopener noreferrer'
        className='hover:bg-neutral-subtle flex items-center gap-3 px-3 py-2.5 transition-colors first:rounded-t-md last:rounded-b-md'
    >
        <span className='flex size-5 shrink-0 items-center justify-center'>{icon}</span>
        <span className='text-label-sm-regular text-dark'>{label}</span>
    </a>
);
